// VIDEO-EDITING — Direct HTTP client for the captions propose + apply routes.
//
// These wrap `POST /core/video-editing/captions/propose` and
// `POST /core/video-editing/captions/apply`. The "Auto add captions" panel
// calls these directly instead of going through Marcus chat, so the panel
// works even when the companion isn't mounted.
//
// All responses are Zod-validated at the boundary.
import { z } from "zod";

import { apiFetch } from "@/lib/jwt-auth-client";

import type {
  CaptionPresetId,
  CaptionsCustomStyle,
  CaptionsProposal,
  CaptionsProposalSignals,
} from "../store/useCaptionsProposalsStore";

const CAPTION_PRESET_IDS = [
  "trust-bar",
  "hero-headline",
  "word-by-word-accent",
  "two-line-conversational",
  "quote-card",
  "sticker-bar",
  "hook-body-stack",
  "caption-lite",
] as const;

const SignalsSchema: z.ZodType<CaptionsProposalSignals> = z.object({
  aspectRatio: z.enum(["9:16", "1:1", "16:9", "other"]),
  durationSec: z.number(),
  segmentCount: z.number(),
  meanWordsPerSegment: z.number(),
  meanWordsPerSec: z.number(),
  contentTone: z.enum(["energetic", "narrative", "educational", "ad"]),
  hasWordTimings: z.boolean(),
  dominantBrandColor: z.string().nullable(),
  productRefAttached: z.boolean(),
  tier: z.enum(["rules", "rules+haiku"]),
});

const CustomStyleSchema: z.ZodType<CaptionsCustomStyle> = z.object({
  positionY: z.number().optional(),
  fontSize: z.number().optional(),
  color: z.string().optional(),
  fontFamily: z.string().optional(),
  fontWeight: z.number().optional(),
});

const ProposalSchema: z.ZodType<CaptionsProposal> = z.object({
  presetId: z.enum(CAPTION_PRESET_IDS),
  customStyle: CustomStyleSchema,
  rationale: z.string(),
  signals: SignalsSchema,
});

const ProposeResponseSchema = z.object({
  action: z.literal("video_captions_proposed"),
  projectId: z.string(),
  sourceActionId: z.string(),
  proposal: ProposalSchema.nullable(),
  proposalCount: z.union([z.literal(0), z.literal(1)]),
  transcriptStatus: z.enum(["reused", "generated", "refreshed"]),
  note: z.string().optional(),
});

const ApplyResponseSchema = z.object({
  action: z.literal("video_captions_applied"),
  projectId: z.string(),
  presetId: z.enum(CAPTION_PRESET_IDS).nullable(),
  customStyle: CustomStyleSchema.nullable(),
});

export type ProposeCaptionsResponse = z.infer<typeof ProposeResponseSchema>;
export type ApplyCaptionsResponse = z.infer<typeof ApplyResponseSchema>;

export interface ProposeCaptionsRequest {
  projectId: string;
  refresh?: boolean;
  forcePresetId?: CaptionPresetId;
}

export interface ApplyCaptionsRequest {
  projectId: string;
  presetId: CaptionPresetId | null;
  customStyle: CaptionsCustomStyle | null;
}

async function jsonOrThrow<T>(res: Response, schema: z.ZodType<T>): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg =
      (body as { error?: string }).error ?? `Request failed (${res.status})`;
    throw new Error(msg);
  }
  const data: unknown = await res.json();
  return schema.parse(data);
}

export async function proposeCaptions(
  body: ProposeCaptionsRequest,
  signal?: AbortSignal,
): Promise<ProposeCaptionsResponse> {
  const res = await apiFetch("/core/video-editing/captions/propose", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  });
  return jsonOrThrow(res, ProposeResponseSchema);
}

export async function applyCaptions(
  body: ApplyCaptionsRequest,
  signal?: AbortSignal,
): Promise<ApplyCaptionsResponse> {
  const res = await apiFetch("/core/video-editing/captions/apply", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  });
  return jsonOrThrow(res, ApplyResponseSchema);
}
